import Button from '@allesauto/core/button';
import Checkbox from '@allesauto/core/checkbox';
import CircularLoader, {
  CircularLoaderSize,
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import CtaTeaser from '@allesauto/core/ctaTeaser';
import InputField from '@allesauto/core/inputField';
import translate from '@allesauto/i18n';
import { useNewsletter } from '@allesauto/utils/apiHooks';
import { CtaTeaserTypes, ToastifyType } from '@allesauto/utils/consts';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import { useFormik } from 'formik';
import Markdown from 'markdown-to-jsx';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import ReCaptchaValidator, {
  ITokenValidator,
} from '@/components/ReCaptcha/ReCaptchaValidator';

import { markdownProps } from '@/utils/markdown';

import validationSchema from '../validation/newsletterFormSchema';

export interface NewsletterTeaserFormProps {
  headline: string;
  description: string;
  isDarkMode?: boolean;
  component: string;
  _uid: string;
  _editable?: string;
}

interface FormValues {
  email: string;
  hasConsent: boolean;
}

const NewsletterTeaserForm: FC<
  React.PropsWithChildren<NewsletterTeaserFormProps>
> = (props) => {
  const { headline, description, isDarkMode = false } = props;
  const intl = useIntl();

  const [subscribe, { result: isSubscribed, loading, error }] = useNewsletter();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [resultEmail, setResultEmail] = useState('');

  const tokenValidator = useRef<ITokenValidator | undefined>();
  const recaptchaToken = useRef<string | null>(null);

  const setTokenValidator = useCallback((v: ITokenValidator) => {
    tokenValidator.current = v;
  }, []);

  useEffect(() => {
    if (error) {
      import('@allesauto/core/toastify').then((module) => {
        module.toastifyFactory(
          ToastifyType.ERROR,
          translate(intl, 'global.ERRORS.NEWSLETTER.HEADER'),
          translate(intl, 'global.ERRORS.NEWSLETTER.TEXT')
        );
      });
    }
  }, [intl, error]);

  const handleSubmit = (values: FormValues) => {
    subscribe({
      type: 'subscribe',
      email: values.email,
      source: 'SubscribeForm',
      recaptchaToken: recaptchaToken.current,
    });

    setResultEmail(formik.values.email);

    formik.values.email = '';
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      email: '',
      hasConsent: false,
    },
    validationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const className = {
    teaserBg: clsx(
      isDarkMode
        ? 'bg-purple80 monochrome:bg-grey90'
        : 'bg-white monochrome:bg-white'
    ),
    teaserText: clsx(
      isDarkMode
        ? 'text-white monochrome:text-white'
        : 'text-grey100 monochrome:text-grey90'
    ),
    inputButton: 'mb-30px flex justify-between flex-col lg:flex-row',
    input: 'flex-1',
    button: 'flex-1 ml-0 mt-10px lg:ml-30px lg:mt-0',
    privacyConsent: clsx(
      'teaser-newsletter-legal',
      isDarkMode && 'teaser-newsletter-legal--dark',
      !!(formik.touched.hasConsent && formik.errors.hasConsent) &&
        'newsletter-legal--error'
    ),
    resultMessage: clsx('text-p1', isDarkMode ? 'text-white' : 'text-grey80'),
    customCtaTeaser: 'my-40px',
  };

  const onNewsletterSignup = useCallback(async () => {
    if (process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA) {
      if (tokenValidator.current) {
        recaptchaToken.current = await tokenValidator.current.validate();
      }
      if (!recaptchaToken.current) {
        return;
      }
    }
    if (!loading) {
      formik.submitForm();
    }
  }, [formik, loading]);

  return (
    <CtaTeaser
      teaserType={CtaTeaserTypes.FRAME}
      headline={headline}
      description={!isSubscribed ? description : undefined}
      bgClassName={className.teaserBg}
      textClassName={className.teaserText}
      className={className.customCtaTeaser}
      component={''}
      _uid={''}
    >
      {isSubscribed ? (
        <p className={className.resultMessage}>
          {translate(intl, 'ui.FORMS.NEWSLETTER.TEASER.RESULT')}
        </p>
      ) : (
        <div {...sbEditable(props)}>
          <div className={className.inputButton}>
            <div className={className.input}>
              <InputField
                id="aa-newsletter--email"
                label={translate(
                  intl,
                  'ui.FORMS.NEWSLETTER.TEASER.INPUT_LABEL'
                )}
                placeholder={translate(
                  intl,
                  'ui.FORMS.NEWSLETTER.TEASER.INPUT_PLACEHOLDER'
                )}
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                errorMessage={!!(formik.touched.email && formik.errors.email)}
                darkBg={isDarkMode}
              />
            </div>
            <div className={className.button}>
              <Button onClick={onNewsletterSignup} fullWidth>
                {loading ? (
                  <CircularLoader
                    type={CircularLoaderType.primary}
                    customSize={CircularLoaderSize.sm}
                  />
                ) : (
                  translate(intl, 'ui.FORMS.NEWSLETTER.TEASER.BUTTON')
                )}
              </Button>
            </div>
          </div>

          <Checkbox
            id="aa-newsletter--gdpr"
            label={
              <Markdown {...markdownProps} className={className.privacyConsent}>
                {translate(intl, 'ui.FORMS.NEWSLETTER.TEASER.PRIVACY_CONSENT')}
              </Markdown>
            }
            checked={formik.values.hasConsent}
            onChange={(value: boolean) =>
              formik.setFieldValue('hasConsent', value)
            }
            invalid={formik.touched.hasConsent && !!formik.errors.hasConsent}
            darkBg={isDarkMode}
          />
          {process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA && (
            <ReCaptchaValidator
              legalTextColor={isDarkMode ? 'lightPurple' : 'grey60'}
              legalLinkColor={isDarkMode ? 'white' : 'grey80'}
              setStepValidator={setTokenValidator}
            />
          )}
        </div>
      )}
    </CtaTeaser>
  );
};

export default NewsletterTeaserForm;
